import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { selectTab } from '@/utils'
import type { ReservationWidget as ReservationWidgetModel } from '@sevenrooms/domain'
import { useLocales } from '@sevenrooms/locales'
import { Flex, Tab, TabList, Tabs, VStack, TabPanel } from '@sevenrooms/ui-kit/layout'
import { useAnalyticsSettings, useReservationsRoute, useVenue } from '../../../hooks'
import { reservationWidgetMessages } from '../../../reservationWidgetMessages'
import { useReservationFormState } from '../../../store'
import { ExperiencesTab } from '../ExperiencesTab'
import { PrivateEventsTab } from '../PrivateEventsTab'
import { ReservationsTab } from '../ReservationsTab'

interface ReservationsTabs {
  showPrivateEvents?: boolean
  showExperiences?: boolean
  isModifyRoute?: boolean
}

export function ReservationsTabs({ showPrivateEvents, showExperiences, isModifyRoute }: ReservationsTabs) {
  const { formatMessage } = useLocales()
  const {
    formState: { searchTab },
    updateFormState,
  } = useReservationFormState()
  const { urlKey: venueUrlKey } = useVenue()
  const { fbPixelIdsAll } = useAnalyticsSettings()
  const { updateQuery } = useReservationsRoute()
  const TABS_DISPLAY_NAMES: Record<ReservationWidgetModel.SearchTab, string> = useMemo(
    () => ({
      reservations: formatMessage(reservationWidgetMessages.resWidgetPrivateEventsReservationsTabLabel),
      group_bookings: formatMessage(reservationWidgetMessages.resWidgetPrivateEventsGroupBookingsTabLabel),
      experiences: formatMessage(reservationWidgetMessages.resWidgetExperiencesTabLabel),
    }),
    [formatMessage]
  )
  const tabs = useMemo(() => {
    const tabs = [
      {
        id: 'reservations',
        text: TABS_DISPLAY_NAMES.reservations,
      },
    ]
    if (showPrivateEvents) {
      tabs.push({
        id: 'group_bookings',
        text: TABS_DISPLAY_NAMES.group_bookings,
      })
    }
    if (showExperiences) {
      tabs.push({
        id: 'experiences',
        text: TABS_DISPLAY_NAMES.experiences,
      })
    }
    return tabs
  }, [TABS_DISPLAY_NAMES, showExperiences, showPrivateEvents])
  const selectedTabIndex = useMemo(() => tabs.findIndex(({ id }) => id === searchTab), [searchTab, tabs])
  const onTabChange = useCallback(
    (index: number) => {
      const searchTab = tabs[index]?.id as ReservationWidgetModel.SearchTab
      updateFormState({ searchTab })
      updateQuery({ searchTab })
      selectTab(venueUrlKey, searchTab, fbPixelIdsAll)
    },
    [fbPixelIdsAll, tabs, updateFormState, updateQuery, venueUrlKey]
  )
  return (
    <StyledTabs>
      <Tabs onSelect={onTabChange} selectedIndex={selectedTabIndex}>
        <StickyContainer>
          <TabList>
            {tabs.map(tab => (
              <Tab key={tab.id} data-test={`sr-${tab.id.toLowerCase()}-tab`}>
                {tab.text}
              </Tab>
            ))}
          </TabList>
          <StyledSpacer width="100%" backgroundColor="primaryBackground" />
        </StickyContainer>
        <TabPanel data-test="sr-reservations-tab-content">
          <ReservationsTab isModifyRoute={isModifyRoute} showPrivateEvents />
        </TabPanel>
        {showPrivateEvents && (
          <TabPanel data-test="sr-private-events-tab-content">
            <PrivateEventsTab showTabs />
          </TabPanel>
        )}
        {showExperiences && (
          <TabPanel data-test="sr-experiences-tab-content">
            <ExperiencesTab showTabs />
          </TabPanel>
        )}
      </Tabs>
    </StyledTabs>
  )
}

const StyledSpacer = styled(Flex)`
  height: ${props => props.theme.spacing.sm};
`

const StickyContainer = styled(VStack)`
  position: sticky;
  z-index: ${props => props.theme.zIndex.navbar + 1};
  top: ${props => `calc(${props.theme.topNav.height.desktop} + 1px)`};

  @media (max-width: ${props => props.theme.breakpoints.m}px) {
    top: ${props => `calc(${props.theme.topNav.height.mobile} + 1px)`};
  }

  background-color: ${props => props.theme.colors.primaryBackground};
  width: 100%;
`

const StyledTabs = styled(VStack)`
  height: 100%;

  // Specify height for Tabs
  & > * {
    height: 100%;
  }

  ${TabList} {
    width: 100%;
  }

  ${Tab} {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: ${props => `${props.theme.spacing.sm} ${props.theme.spacing.lm}`};
    font: ${props => props.theme.textStyles.h3};
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;

    &:not(.selected) {
      font-weight: 400;
    }
  }

  ${Tab} + ${Tab} {
    margin-left: 0;
  }

  @media (max-width: ${props => props.theme.breakpoints.xs}px) {
    ${Tab} {
      padding-left: 6px;
      padding-right: ${props => props.theme.spacing.xs};
    }
  }
`
